<template>
    <!-- Sidebar -->
    <v-fade-transition mode="out-in">
        <VerticalSidebar v-if="showComponent" class="sidebar" />
    </v-fade-transition>
    <!-- Main Content Wrapper -->
    <!-- <v-container> -->
    <!-- Header -->
    <v-slide-y-transition mode="out-in">
        <HorizontalHeader v-if="showComponent" class="header" />
    </v-slide-y-transition>
    <!-- Main Content -->
    <v-slide-x-reverse-transition mode="out-in">
        <v-main v-if="showComponent">
            <v-container
                fluid
                :class="{ 'dashboard-main-container--flush-bottom': flushMainBottom }"
            >
                <router-view v-slot="{ Component }">
                    <div class="h-100">
                        <v-slide-x-reverse-transition
                            name="slide-right"
                            mode="out-in"
                        >
                            <div
                                :key="$route.fullPath"
                                class="position-relative h-100"
                            >
                                <component :is="Component" />
                                <ANPCBanner
                                    :key="$route.fullPath"
                                    v-if="
                                        $route.name === 'IncidentsView' ||
                                        $route.name === 'AddPaymentIncident' ||
                                        $route.name === 'AddTransportIncident'
                                    "
                                    class="anpc-layout-fixed"
                                />
                            </div>
                        </v-slide-x-reverse-transition>
                    </div>
                </router-view>
            </v-container>
        </v-main>
    </v-slide-x-reverse-transition>
</template>

<script>
import HorizontalHeader from "./vertical-header/HorizontalHeader.vue";
import VerticalSidebar from "./vertical-sidebar/VerticalSidebar.vue";
import { mapState } from "vuex";
import emitter from "@/eventBus";
import ANPCBanner from "@/components/display/ANPCBanner.vue";
import {
    showAlertModal,
    startUpdateWatcher,
    UPDATE_ACTION_STORAGE_KEY,
    applyUpdateAction,
    shouldApplyCrossTabUpdatePayload,
} from "@/utils";
import { getAccessToken } from "@/auth/sessionTokens";
import { hardLogout } from "@/api";

// Inactivity logout: from env (e.g. 60000 dev, 3600000 prod)
const INACTIVITY_MS = Number(process.env.VUE_APP_INACTIVITY_MS);
const MOUSEMOVE_THROTTLE_MS = 3000;

export default {
    components: {
        ANPCBanner,
        HorizontalHeader,
        VerticalSidebar,
    },
    data() {
        return {
            showComponent: false,
            socket: null, // Will hold our WebSocket connection
            inactivityTimeoutId: null,
            inactivityListeners: [],
            lastMousemoveReset: 0,
            updateWatcherIntervalId: null,
        };
    },
    mounted() {
        this.showComponent = true;

        if (getAccessToken()) {
            this.startInactivityTimer();
            this.updateWatcherIntervalId = startUpdateWatcher();
            window.addEventListener("storage", this.onStorageUpdateAction);
        }

        const wsUrl =
            process.env.VUE_APP_API_URL.replace(/^(https?):\/\//, (_, proto) =>
                proto === "https" ? "wss://" : "ws://"
            ).replace(/\/api\/?$/, "") + "/ws/updates/";

        this.socket = new WebSocket(wsUrl);

        this.socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                emitter.emit("ws-update", data);
            } catch (err) {
                console.error("WebSocket JSON error:", err);
                this.showAlertModal(
                    this.$store,
                    "WebSocket JSON error",
                    "danger",
                    12000,
                    err
                );
            }
        };

        this.socket.onerror = (err) => {
            console.error("WebSocket error:", err);
            this.showAlertModal(
                this.$store,
                "WebSocket error",
                "danger",
                12000,
                err
            );
        };
    },
    beforeUnmount() {
        this.clearInactivityTimer();
        if (this.updateWatcherIntervalId) {
            clearInterval(this.updateWatcherIntervalId);
            this.updateWatcherIntervalId = null;
        }
        window.removeEventListener("storage", this.onStorageUpdateAction);
        // Clean up the WebSocket connection when the component is destroyed
        if (this.socket) {
            this.socket.close();
        }
    },
    computed: {
        ...mapState({
            mini_sidebar: (state) => state.mini_sidebar, // Ensures `mini_sidebar` is correctly linked to Vuex
        }),
        flushMainBottom() {
            return Boolean(this.$route.meta?.flushMainBottom);
        },
    },
    methods: {
        showAlertModal,
        /**
         * Handle cross-tab update actions emitted through localStorage.
         *
         * @param {StorageEvent} event - Browser storage event.
         * @returns {void}
         */
        onStorageUpdateAction(event) {
            if (event.key !== UPDATE_ACTION_STORAGE_KEY || !event.newValue) return;

            let payload;
            try {
                payload = JSON.parse(event.newValue);
            } catch {
                return;
            }

            if (!shouldApplyCrossTabUpdatePayload(payload)) return;

            applyUpdateAction(payload.action, payload.timestamp);
        },
        /**
         * Clears any pending inactivity timeout and schedules a new one.
         * When the timeout fires after INACTIVITY_MS with no activity, calls hardLogout("loggedOutInactivity").
         */
        resetInactivityTimer() {
            if (this.inactivityTimeoutId) {
                clearTimeout(this.inactivityTimeoutId);
                this.inactivityTimeoutId = null;
            }
            this.inactivityTimeoutId = setTimeout(() => {
                this.inactivityTimeoutId = null;
                hardLogout("loggedOutInactivity");
            }, INACTIVITY_MS);
        },
        /**
         * Called when the user performs an activity (click, keydown, touchstart, scroll).
         * Resets the inactivity countdown so logout is delayed.
         */
        onActivity() {
            this.resetInactivityTimer();
        },
        /**
         * Called on mousemove. Resets the inactivity countdown at most once per MOUSEMOVE_THROTTLE_MS
         * so we do not reset the timer hundreds of times per second.
         */
        onMousemove() {
            const now = Date.now();
            if (now - this.lastMousemoveReset >= MOUSEMOVE_THROTTLE_MS) {
                this.lastMousemoveReset = now;
                this.resetInactivityTimer();
            }
        },
        /**
         * Starts the inactivity logout: schedules the first timeout and attaches document
         * listeners for click, keydown, touchstart, scroll, and throttled mousemove.
         * Call clearInactivityTimer() on unmount or logout to remove listeners and clear the timeout.
         */
        startInactivityTimer() {
            this.resetInactivityTimer();
            const events = ["click", "keydown", "touchstart", "scroll"];
            events.forEach((eventName) => {
                const handler = () => this.onActivity();
                document.addEventListener(eventName, handler);
                this.inactivityListeners.push({ eventName, handler });
            });
            const mousemoveHandler = () => this.onMousemove();
            document.addEventListener("mousemove", mousemoveHandler);
            this.inactivityListeners.push({
                eventName: "mousemove",
                handler: mousemoveHandler,
            });
        },
        /**
         * Clears the pending inactivity timeout and removes all activity listeners from document.
         * It is called in beforeUnmount so we do not leave global listeners or a dangling timeout.
         */
        clearInactivityTimer() {
            if (this.inactivityTimeoutId) {
                clearTimeout(this.inactivityTimeoutId);
                this.inactivityTimeoutId = null;
            }
            this.inactivityListeners.forEach(({ eventName, handler }) => {
                document.removeEventListener(eventName, handler);
            });
            this.inactivityListeners = [];
        },
    },
};
</script>

<style>
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.2s ease, opacity 0.2s ease;
    position: absolute;
    width: 100%;
}

.slide-right-enter-from {
    transform: translateX(40px);
    opacity: 0;
}

.slide-right-leave-to {
    transform: translateX(-40px);
    opacity: 0;
}

.slide-right-enter-to,
.slide-right-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.view-wrapper {
    position: relative;
}

.anpc-layout-fixed {
    position: sticky;
    bottom: -32px;
    left: 0;
    right: 0;
    z-index: 5;
    background-color: transparent;
    padding: 16px 32px;
    max-width: none;
    width: 100%;
}
</style>